import React, { useState, useEffect, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import { TextBody } from "@learningblocks/interactives/interactive-infographic-components/lib/TextBody";
import { useUsersnap } from "@labxchange/util-usersnap";
import {
  sanitizeUnsafeHTML,
  sanitizeConfigOptions,
} from "@labxchange/util-sanitization";

import { DropdownHotspot } from "./components/DropdownHotspot";
import { StandardHotspot } from "./components/StandardHotspot";
import {
  ContentBoxTriggerIndicator,
  ContentBoxCorrectnessIndicator,
} from "./components/ContentBoxElements";
import {
  createClickHandler,
  initTooltipDirection,
  checkTextBodySize,
  isDropdownHotspot,
  getHotspotClassNames,
} from "./utils";
import { decodeHtmlEntities } from "../utils/stringUtils";

import className from "../style.module.scss";

export function Hotspot(props) {
  const intl = useIntl();
  const hotspotRef = useRef();
  const { loadUsersnap } = useUsersnap();

  const [tooltipDirection, setTooltipDirection] = useState("top-right");

  const [textBodyIsSmall, setTextBodyIsSmall] = useState(false);
  const [textBodyWidth, setTextBodyWidth] = useState(0);
  const [textBodyHeight, setTextBodyHeight] = useState(0);

  const ariaLabel = useMemo(() => {
    const lockedText =
      props.locked && !props.data.triggered
        ? intl.formatMessage({ id: "Locked", defaultMessage: "Locked" })
        : "";

    const tooltipText = props.data.tooltipText
      ? decodeHtmlEntities(
          sanitizeUnsafeHTML(
            intl.formatMessage({
              id: props.data.tooltipText,
              defaultMessage: props.data.tooltipText,
            }),
            sanitizeConfigOptions.PlainText
          )
        )
      : "";

    const mainText = lockedText || tooltipText;

    const hotspotText = intl.formatMessage({
      id: "hotspot",
      defaultMessage: "hotspot",
    });

    const statusText =
      props.data.triggered &&
      (props.data.postTrigger !== "inactive" ||
        props.data.postTrigger !== "no-change")
        ? intl.formatMessage({ id: "checked", defaultMessage: "checked" })
        : intl.formatMessage({ id: "unchecked", defaultMessage: "unchecked" });

    return `${mainText} ${hotspotText} [${statusText}]`;
  }, [
    intl,
    props.locked,
    props.data.triggered,
    props.data.tooltipText,
    props.data.postTrigger,
  ]);

  useEffect(() => {
    initTooltip();

    if (props.data.props.type === "text-body") {
      setTextBodyWidth(0);
      setTextBodyIsSmall(true);
      const timer1 = setTimeout(() => {
        handleTextBodySizeCheck();
      }, props.transitionSpeed * 1000);
      window.addEventListener("resize", handleTextBodySizeCheck);

      return () => {
        clearTimeout(timer1);
        window.removeEventListener("resize", handleTextBodySizeCheck);
      };
    }
  }, []);

  const initTooltip = () => {
    const direction = initTooltipDirection(props.data, props.viewData);
    setTooltipDirection(direction);
  };

  const handleTextBodySizeCheck = () => {
    checkTextBodySize(
      props.id,
      props.data,
      props.isDiabledBlurOnSmall,
      setTextBodyWidth,
      setTextBodyHeight,
      setTextBodyIsSmall
    );
  };

  const handleClick = createClickHandler({
    ...props,
    loadUsersnap,
  });

  const renderHotSpot = () => {
    return <StandardHotspot {...props} tooltipDirection={tooltipDirection} />;
  };

  if (isDropdownHotspot(props.data)) {
    return <DropdownHotspot {...props} tooltipDirection={tooltipDirection} />;
  }

  return (
    <>
      {props.data.props.type === "content-box" && (
        <ContentBoxTriggerIndicator
          selectedHotspot={props.selectedHotspot}
          id={props.id}
        />
      )}
      {props.data.props.type === "content-box" && (
        <ContentBoxCorrectnessIndicator
          isCorrect={props.isCorrect}
          hotspotRef={hotspotRef}
          handleClick={() => {
            props.setLatestHotspotRef(hotspotRef);
            handleClick();
          }}
        />
      )}
      <button
        id={`hotspotBtn_${props.id}`}
        ref={hotspotRef}
        className={getHotspotClassNames(props, className)}
        disabled={
          (props.data.triggered && props.data.postTrigger === "inactive") ||
          (props.locked && !props.data.triggered)
        }
        tabIndex={
          props.data.visibility_dependencies.views.includes(
            props.currentView
          ) || props.data.visibility_dependencies.views.length === 0
            ? 0
            : -1
        }
        onMouseDown={() => {
          props.setLatestHotspotRef(hotspotRef);
          handleClick();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            props.setLatestHotspotRef(hotspotRef);
            handleClick();
          }
        }}
        aria-label={ariaLabel}
      >
        {props.data.props.type === "text-body" ? (
          <TextBody
            id={props.id}
            textBodyIsSmall={textBodyIsSmall}
            textBodyWidth={textBodyWidth}
            textBodyHeight={textBodyHeight}
            text={props.data.props.text}
            theme={props.data.props.theme}
          />
        ) : null}
        {props.data.props.type === "text-body" && !textBodyIsSmall
          ? null
          : renderHotSpot()}
      </button>
    </>
  );
}

Hotspot.propTypes = {
  data: PropTypes.shape({
    props: PropTypes.shape({
      type: PropTypes.string.isRequired,
    }).isRequired,
    triggered: PropTypes.bool.isRequired,
    postTrigger: PropTypes.string.isRequired,
  }).isRequired,
  transitionSpeed: PropTypes.number.isRequired,
  viewData: PropTypes.shape({
    x_offset: PropTypes.number.isRequired,
    y_offset: PropTypes.number.isRequired,
    size: PropTypes.shape({
      width: PropTypes.number.isRequired,
      height: PropTypes.number.isRequired,
    }),
  }).isRequired,
  id: PropTypes.string.isRequired,
  locked: PropTypes.bool.isRequired,
  hide: PropTypes.bool.isRequired,
  showGrid: PropTypes.bool.isRequired,
  idle: PropTypes.bool.isRequired,
  isDiabledBlurOnSmall: PropTypes.bool.isRequired,
  selectedHotspot: PropTypes.number,
  usersnapProjectId: PropTypes.string,
};
